import React, { ReactNode } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { Theme } from '@mui/material/styles';
import { AppStore } from '@gamiphy/loyalty-station-desktop/store/app';
import { createPortal } from 'react-dom';
import { addElementInteractListeners } from '@gamiphy/utils/interaction';
import { Box, SxProps } from '@mui/material';
import Portal from './Portal';

interface Props {
  onRender?: (node: HTMLIFrameElement | null) => void,
  sx?: SxProps<Theme>,
  className?: string,
  stylesheets?: string[],
  theme: Theme,
  children?: React.ReactNode,
  iframeID?: string
}

class FramePortal extends React.Component<Props> {
  private readonly frameKey = uuidv4();

  private node?: HTMLIFrameElement;

  private window?: Window;

  private doc?: Document;

  componentDidMount(): void {
    if (this.node) {
      this.node.addEventListener('load', this.handleLoad.bind(this));
    }
  }

  componentWillUnmount(): void {
    if (this.node) {
      this.node.removeEventListener('load', this.handleLoad.bind(this));
    }
  }

  handleLoad(): void {
    if (this.node?.contentDocument) {
      this.window = this.node.contentWindow ?? undefined;
      this.doc = this.node.contentDocument;
      this.doc.dir = AppStore.dir;

      addElementInteractListeners(this.node.contentDocument.body);
    }

    this.forceUpdate();
  }

  render(): ReactNode {
    const {
      children,
      theme,
      sx,
      className,
      stylesheets,
      onRender,
      iframeID,
    } = this.props;

    return (
      <Box
        component="iframe"
        sx={{
          width: '100%',
          height: '100%',
          border: 'none',
          paddingTop: 0,
          marginTop: 0,
          ...sx,
        }}
        className={className}
        title="main"
        srcDoc="<!DOCTYPE html>"
        id={iframeID}
        translate="no"
        ref={(node: HTMLIFrameElement) => {
          this.node = (node ?? undefined);
          if (onRender) {
            onRender(node);
          }
        }}
      >
        {
          this.doc && createPortal(
            <Portal
              key={this.frameKey}
              window={this.window ?? undefined}
              document={this.doc}
              theme={theme}
            >
              {children}
            </Portal>,
            this.doc.body,
          )
        }
        {
          this.doc && createPortal(
            stylesheets?.map((s) => (
              <link key={s} href={s} rel="stylesheet" type="text/css" />
            )),
            this.doc.head,
          )
        }
      </Box>
    );
  }
}

export default FramePortal;
